import React, { useState, useRef, useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';

interface DatePickerProps {
  value: string;
  onChange: (date: string) => void;
  min?: string;
  max?: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  min,
  max,
  placeholder = 'Select date',
  className = '',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(dayjs(value || undefined));
  const pickerRef = useRef<HTMLDivElement>(null);

  // Update currentMonth when value prop changes
  useEffect(() => {
    if (value) {
      setCurrentMonth(dayjs(value));
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const selectedDate = value ? dayjs(value) : null;
  const minDate = min ? dayjs(min) : null;
  const maxDate = max ? dayjs(max) : null;

  // Generate years list
  const getYears = (): number[] => {
    const currentYear = dayjs().year();
    const minYear = minDate ? minDate.year() : currentYear - 100;
    const maxYear = maxDate ? maxDate.year() : currentYear + 10;
    const years: number[] = [];
    for (let year = maxYear; year >= minYear; year--) {
      years.push(year);
    }
    return years;
  };

  // Generate months list
  const getMonths = (): { value: number; label: string }[] => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months.map((month, index) => ({ value: index, label: month }));
  };

  const years = getYears();
  const months = getMonths();

  const daysInMonth = currentMonth.daysInMonth();
  const firstDayOfMonth = currentMonth.startOf('month').day();
  const days: (Dayjs | null)[] = [];

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null);
  }

  // Add all days of the month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(currentMonth.date(i));
  }

  const isDateDisabled = (date: Dayjs): boolean => {
    if (minDate && date.isBefore(minDate, 'day')) return true;
    if (maxDate && date.isAfter(maxDate, 'day')) return true;
    return false;
  };

  const isDateSelected = (date: Dayjs): boolean => {
    return selectedDate ? date.isSame(selectedDate, 'day') : false;
  };

  const isToday = (date: Dayjs): boolean => {
    return date.isSame(dayjs(), 'day');
  };

  const handleDateSelect = (date: Dayjs) => {
    if (!isDateDisabled(date)) {
      onChange(date.format('YYYY-MM-DD'));
      setIsOpen(false);
    }
  };

  const handlePrevMonth = () => {
    const prevMonth = currentMonth.subtract(1, 'month');
    if (!minDate || prevMonth.isAfter(minDate, 'month') || prevMonth.isSame(minDate, 'month')) {
      setCurrentMonth(prevMonth);
    }
  };

  const handleNextMonth = () => {
    const nextMonth = currentMonth.add(1, 'month');
    if (!maxDate || nextMonth.isBefore(maxDate, 'month') || nextMonth.isSame(maxDate, 'month')) {
      setCurrentMonth(nextMonth);
    }
  };
  /*
  const handleYearChange = (year: number) => {
    const newDate = currentMonth.year(year);
    // Ensure the new date is within min/max constraints
    if (minDate && newDate.isBefore(minDate, 'month')) {
      setCurrentMonth(minDate);
    } else if (maxDate && newDate.isAfter(maxDate, 'month')) {
      setCurrentMonth(maxDate);
    } else {
      setCurrentMonth(newDate);
    }
  };
  */

  const handleYearChange = (year: number) => {
    setCurrentMonth(
      currentMonth
        .clone()
        .date(1)
        .year(year)
    );
  };

  /*
  const handleMonthChange = (monthIndex: number) => {
    const newDate = currentMonth.month(monthIndex);
    // Ensure the new date is within min/max constraints
    if (minDate && newDate.isBefore(minDate, 'month')) {
      setCurrentMonth(minDate);
    } else if (maxDate && newDate.isAfter(maxDate, 'month')) {
      setCurrentMonth(maxDate);
    } else {
      setCurrentMonth(newDate);
    }
  };
  */

  const handleMonthChange = (monthIndex: number) => {
    setCurrentMonth(
      currentMonth
        .clone()
        .date(1)
        .month(monthIndex)
    );
  };

  const formatDisplayDate = (): string => {
    if (!value) return '';
    try {
      const date = dayjs(value);
      if (!date.isValid()) return '';
      return date.format('MMM DD, YYYY');
    } catch {
      return '';
    }
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className={`relative ${className}`} ref={pickerRef}>
      {/* Input Field */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`
          dark-input w-full px-3 py-2 rounded-md
          cursor-pointer flex items-center justify-between
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400 dark:hover:border-gray-500'}
        `}
      >
        <span className={value ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-400'}>
          {value ? formatDisplayDate() : placeholder}
        </span>
        <i className="fa-solid fa-calendar-days text-gray-400 dark:text-gray-400 px-2"></i>
      </div>

      {/* Calendar Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 dark:border-opacity-80 p-4 w-80 transform transition-all duration-200 ease-out opacity-100 scale-100">
          {/* Header with Year and Month Selectors */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={handlePrevMonth}
                disabled={minDate ? (currentMonth.isBefore(minDate, 'month') || currentMonth.isSame(minDate, 'month')) : false}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                type="button"
              >
                <i className="fa-solid fa-chevron-left"></i>
              </button>
              <div className="flex items-center gap-2 flex-1 justify-center">
                <select
                  value={currentMonth.month()}
                  onChange={(e) => handleMonthChange(Number(e.target.value))}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 cursor-pointer"
                >
                  {months.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
                <select
                  value={currentMonth.year()}
                  onChange={(e) => handleYearChange(Number(e.target.value))}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 cursor-pointer"
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleNextMonth}
                disabled={maxDate ? (currentMonth.isAfter(maxDate, 'month') || currentMonth.isSame(maxDate, 'month')) : false}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                type="button"
              >
                <i className="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          </div>

          {/* Week Days Header */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map((day) => (
              <div
                key={day}
                className="text-center text-xs font-semibold text-gray-500 dark:text-gray-300 py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((date, index) => {
              if (!date) {
                return <div key={index} className="aspect-square" />;
              }

              const disabled = isDateDisabled(date);
              const selected = isDateSelected(date);
              const today = isToday(date);

              return (
                <button
                  key={index}
                  onClick={() => handleDateSelect(date)}
                  disabled={disabled}
                  className={`
                    aspect-square rounded-lg text-sm font-medium transition-all duration-150
                    flex items-center justify-center
                    ${disabled
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                      : selected
                      ? 'bg-blue-600 dark:bg-blue-500 text-white shadow-lg scale-105'
                      : today
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
                    ${!disabled && !selected ? 'hover:scale-105' : ''}
                  `}
                  type="button"
                >
                  {date.date()}
                </button>
              );
            })}
          </div>

          {/* Quick Actions */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex gap-2">
            <button
              onClick={() => {
                onChange(dayjs().format('YYYY-MM-DD'));
                setIsOpen(false);
              }}
              disabled={!!(maxDate && dayjs().isAfter(maxDate, 'day'))}
              className="flex-1 px-3 py-2 text-sm rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              type="button"
            >
              Today
            </button>
            {value && (
              <button
                onClick={() => {
                  onChange('');
                  setIsOpen(false);
                }}
                className="px-3 py-2 text-sm rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                type="button"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;

